// src/pages/ClientEmailSecurity.js — Client Email Security (2026-09-16)
//
// Fleet dashboard + per-client detail for O365 email and mailbox security,
// with the remediation tools attached to the findings that need them.
//
// WHY THIS EXISTS: the Champion Media spoof (2026-09-15) was caused by three
// things no screen in the platform would have shown — an anti-phish RULE
// disabled (leaving a good policy bound to nothing), the tenant's own domains
// sitting in its spam allow list, and 29 of 34 domains with no DMARC record so
// a hard-fail SPF did nothing. Finding those took a manual read of the live
// tenant. This turns that into a scan.
//
// ⚠️ THE TOOLS WRITE TO A CLIENT'S PRODUCTION TENANT. Every one asks for
// confirmation first and is admin-only server-side. There is deliberately no
// "fix everything" button: blanket enforcement breaks legitimate unauthenticated
// senders (web-host circulation mail, marketing platforms), which is exactly the
// trap the Champion investigation avoided.
import { useState, useEffect, useCallback } from "react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const call = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };
const btn = (bg, extra = {}) => ({ background: bg, color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", ...extra });
const SEV = {
  critical: { c: "#f87171", bg: "rgba(248,113,113,0.13)", label: "CRITICAL" },
  high:     { c: "#fb923c", bg: "rgba(251,146,60,0.13)",  label: "HIGH" },
  medium:   { c: "#fbbf24", bg: "rgba(251,191,36,0.13)",  label: "MEDIUM" },
  low:      { c: "#60a5fa", bg: "rgba(96,165,250,0.13)",  label: "LOW" },
};
const scoreColor = (s) => s == null ? "#64748b" : s >= 90 ? "#34d399" : s >= 70 ? "#fbbf24" : s >= 40 ? "#fb923c" : "#f87171";
const ago = (ts) => {
  if (!ts) return "never";
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ ...panel, padding: "13px 15px", minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "var(--t-text-faint)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 25, fontWeight: 800, color: color || "var(--t-text-primary)", lineHeight: 1.25, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>{sub}</div>}
    </div>
  );
}

function Bar({ label, have, total }) {
  const pct = total ? Math.round((have / total) * 100) : 0;
  const col = pct >= 90 ? "#34d399" : pct >= 50 ? "#fbbf24" : "#f87171";
  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--t-text-body)", marginBottom: 3 }}>
        <span>{label}</span><span style={{ color: col, fontWeight: 700 }}>{have}/{total} · {pct}%</span>
      </div>
      <div style={{ height: 6, background: "var(--t-input)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: col, transition: "width .3s" }} />
      </div>
    </div>
  );
}


// ── MFA tab (mfaAudit.js) ────────────────────────────────────
// Registered ≠ enforced: the banner says whether the tenant REQUIRES MFA, the
// numbers say who has a second factor set up. "Unknown" is shown as unknown.
const ENF = {
  full:        { c: "#34d399", bg: "rgba(52,211,153,0.10)", label: "Enforced tenant-wide" },
  partial:     { c: "#fbbf24", bg: "rgba(251,191,36,0.10)", label: "Partly enforced" },
  none:        { c: "#f87171", bg: "rgba(248,113,113,0.10)", label: "Not enforced" },
  unconfirmed: { c: "#fb923c", bg: "rgba(251,146,60,0.10)", label: "Enforcement unconfirmed" },
  unknown:     { c: "#94a3b8", bg: "rgba(148,163,184,0.10)", label: "Enforcement unknown" },
};
const SOURCE_LABELS = {
  lastSignIn: "Last sign-in", adminRoles: "Admin roles", registrationReport: "Registration report",
  methods: "Per-user methods", securityDefaults: "Security defaults", conditionalAccess: "Conditional Access", perUserMfa: "Per-user MFA",
  signInLogs: "Sign-in logs", sharedMailboxes: "Shared mailbox list (Exchange)",
};
function MfaPanel({ mfa, busy, onRun, onExport }) {
  const [filter, setFilter] = useState("problems");
  const a = mfa?.audit;
  const s = a?.summary || {};
  const users = a?.users || [];
  const enf = ENF[s.enforcement?.level] || ENF.unknown;
  // Standard (Juwan 2026-10-15): MFA by phone (text or call); the app is optional.
  // Shared / room / equipment mailboxes and no-reply senders are out of scope.
  // + service senders: only legacy (SMTP AUTH) sign-ins in 7 days — scanners, printers, order mail.
  const svc = (u) => !!u.serviceSender || !!(u.signin && u.signin.legacy.length && !u.signin.pw && !u.signin.mfa);
  const excluded = (u) => !!u.mailboxType || u.noReply || svc(u);
  const hasPhone = (u) => u.methods.includes("Phone (SMS/call)");
  const pwOnly = (u) => !!(u.signin && u.signin.pw > 0);
  const legacy = (u) => !!(u.signin && u.signin.legacy.length);
  const shown = users.filter(u =>
    filter === "all" ? true
    : filter === "excluded" ? excluded(u)
    : excluded(u) ? false
    : filter === "nomfa" ? u.registered === false
    : filter === "admins" ? u.admin.length > 0
    : filter === "nophone" ? u.registered === true && !hasPhone(u)
    : (u.registered === false || u.admin.length > 0 || pwOnly(u) || legacy(u)));
  const gaps = Object.entries(s.sources || {}).filter(([, v]) => v !== "ok");
  const cell = { padding: "8px 12px", borderBottom: "1px solid var(--t-border-soft)", verticalAlign: "top" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--t-text-faint)", lineHeight: 1.7, maxWidth: 640 }}>
          Who in this tenant can sign in with only a password. Read-only — refreshed daily, or run it now.
          {a?.ran_at && <> Last run {ago(a.ran_at)}.</>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {a?.ok && <button onClick={onExport} disabled={!!busy} style={btn("transparent", { color: "var(--t-text-body)", border: "1px solid var(--t-border-soft)" })}>Export CSV</button>}
          <button onClick={onRun} disabled={!!busy} style={btn("#0d9488", { opacity: busy ? 0.6 : 1 })}>
            {busy === "mfa" ? "Reading tenant… (up to 1 min)" : "Run MFA audit now"}
          </button>
        </div>
      </div>

      {!a && <div style={{ ...panel, padding: 16, fontSize: 13, color: "var(--t-text-faint)" }}>No MFA audit has run for this tenant yet.</div>}
      {a && !a.ok && (
        <div style={{ ...panel, padding: 12, borderColor: "#b45309", background: "rgba(180,83,9,0.12)", color: "#fbbf24", fontSize: 12, lineHeight: 1.6 }}>
          Could not audit this tenant: {a.error}
        </div>
      )}

      {a?.ok && (
        <>
          <div style={{ ...panel, padding: "12px 16px", borderLeft: `3px solid ${enf.c}`, background: enf.bg }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: enf.c }}>{enf.label}</div>
            <div style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 3, lineHeight: 1.6 }}>{s.enforcement?.text}</div>
            {Array.isArray(s.ca_mfa_policies) && s.ca_mfa_policies.length > 0 && (
              <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 6 }}>
                Conditional Access: {s.ca_mfa_policies.map(p => `${p.name}${p.state !== "enabled" ? " (report-only)" : ""}${p.allUsers ? " · all users" : ""}${p.exclusions ? ` · ${p.exclusions} exclusion(s)` : ""}`).join(" | ")}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
            <Stat label="People with MFA" value={s.coverage != null ? `${s.coverage}%` : "—"} sub={`${s.people_registered}/${s.people} licensed people`} color={scoreColor(s.coverage)} />
            <Stat label="People without MFA" value={s.people_no_mfa} color={s.people_no_mfa ? "#fb923c" : "#34d399"} sub={s.unlicensed_no_mfa ? `+${s.unlicensed_no_mfa} unlicensed account(s)` : undefined} />
            <Stat label="Admins w/o MFA" value={s.admins_no_mfa} sub={`${s.admins} admin account(s)`} color={s.admins_no_mfa ? "#f87171" : "#34d399"} />
            <Stat label="Phone on file" value={s.people_with_phone != null ? `${s.people_with_phone}/${s.people}` : "—"} sub="text or call · app optional" />
            <Stat label="Password-only sign-ins" value={s.password_only_7d ?? "—"} sub={s.password_only_7d == null ? "needs Entra P1" : `people, last 7 days${s.legacy_7d ? ` · ${s.legacy_7d} legacy` : ""}`} color={s.password_only_7d ? "#fb923c" : undefined} />
            <Stat label="Excluded" value={(s.shared_mailboxes || 0) + (s.no_reply || 0) + (s.service_senders || 0)} sub={`${s.shared_mailboxes || 0} shared/room · ${s.no_reply || 0} no-reply · ${s.service_senders || 0} SMTP service`} />
          </div>

          {(a.findings || []).length > 0 && (
            <div style={{ display: "grid", gap: 8 }}>
              {a.findings.map((f, i) => (
                <div key={i} style={{ ...panel, padding: 13, borderLeft: `3px solid ${SEV[f.severity]?.c}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: SEV[f.severity]?.bg, color: SEV[f.severity]?.c }}>{SEV[f.severity]?.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>{f.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 4, lineHeight: 1.6, wordBreak: "break-word" }}>{f.detail}</div>
                </div>
              ))}
            </div>
          )}

          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>Accounts that can sign in ({users.length})</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[["problems", "Needs attention"], ["nomfa", "No MFA"], ["nophone", "No phone"], ["admins", "Admins"], ["excluded", "Excluded"], ["all", "All"]].map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} style={{ padding: "4px 10px", borderRadius: 99, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                    border: "1px solid var(--t-border-soft)", background: filter === k ? "#0d9488" : "transparent", color: filter === k ? "#fff" : "var(--t-text-body)" }}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{ ...panel, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 760 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase" }}>
                    {["Account", "Licensed", "Admin", "MFA", "Methods", "MFA at sign-in", "Last sign-in"].map(h =>
                      <th key={h} style={{ ...cell, fontWeight: 600 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && <tr><td colSpan={7} style={{ ...cell, color: "#bbf7d0" }}>Nothing in this view.</td></tr>}
                  {shown.map(u => (
                    <tr key={u.id}>
                      <td style={cell}>
                        <div style={{ fontWeight: 600, color: "var(--t-text-primary)" }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: "var(--t-text-faint)", fontFamily: "ui-monospace,Menlo,monospace" }}>{u.upn}</div>
                        {excluded(u) && <div style={{ fontSize: 10.5, color: "var(--t-text-faint)", marginTop: 2 }}>Excluded · {u.mailboxType ? u.mailboxType.replace("Mailbox", " mailbox").toLowerCase() : u.noReply ? "no-reply" : "service sender (SMTP)"}</div>}
                      </td>
                      <td style={{ ...cell, color: "var(--t-text-body)" }}>{u.licensed ? "Yes" : "No"}</td>
                      <td style={{ ...cell, color: u.admin.length ? "#fb923c" : "var(--t-text-faint)", fontWeight: u.admin.length ? 700 : 400 }}>{u.admin.length ? u.admin.join(", ") : "—"}</td>
                      <td style={{ ...cell, fontWeight: 700, color: u.registered == null ? "var(--t-text-faint)" : u.registered ? "#34d399" : excluded(u) ? "var(--t-text-faint)" : "#f87171" }}>
                        {u.registered == null ? "Unknown" : u.registered ? (hasPhone(u) ? "Yes" : "Yes (app)") : excluded(u) ? "No (excluded)" : "NO"}
                      </td>
                      <td style={{ ...cell, color: "var(--t-text-body)" }}>{u.methods.length ? u.methods.join(", ") : "—"}</td>
                      <td style={{ ...cell, color: legacy(u) || pwOnly(u) ? "#f87171" : u.signin && u.signin.mfa ? "#34d399" : u.enforced == null ? "var(--t-text-faint)" : u.enforced ? "#34d399" : "#f87171" }}>
                        {legacy(u) ? `Legacy sign-in: ${u.signin.legacy.join(", ")}`
                          : pwOnly(u) ? `Password only (${u.signin.pw}× in 7 days)`
                          : u.signin && u.signin.mfa ? "Yes (seen at sign-in)"
                          : u.enforced == null ? "Unknown" : u.enforced ? "Yes" : "No"}
                      </td>
                      <td style={{ ...cell, color: "var(--t-text-faint)" }}>{u.lastSignIn ? ago(u.lastSignIn) : s.stale_90d == null ? "—" : "never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ fontSize: 11, color: "var(--t-text-faint)", lineHeight: 1.7 }}>
            Methods read from the {s.method_source}.{s.guests ? ` ${s.guests} guest account(s) not counted.` : ""}
            {gaps.length > 0 && <> Not readable with this tenant's app: {gaps.map(([k, v]) => `${SOURCE_LABELS[k] || k} (${String(v).slice(0, 90)})`).join("; ")}.</>}
          </div>
        </>
      )}
    </div>
  );
}

// ── Check any domain (public DNS only) ───────────────────────
// Works for ANY company — prospects, vendors, clients without a connected
// tenant. Backend: analyzeDomain() in emailSecurity.js. Read-only.
const SPOOF = {
  spoofable: { c: "#f87171", bg: "rgba(248,113,113,0.12)", label: "SPOOFABLE" },
  partial:   { c: "#fbbf24", bg: "rgba(251,191,36,0.12)",  label: "PARTLY PROTECTED" },
  protected: { c: "#34d399", bg: "rgba(52,211,153,0.12)",  label: "PROTECTED" },
};
const MGMT = { well: "#34d399", partial: "#fbbf24", poor: "#f87171" };
const areaState = (b) => !b ? null : b.pts / b.max >= 0.9 ? "ok" : b.pts / b.max >= 0.4 ? "warn" : "bad";
const STATE = { ok: { c: "#34d399", t: "GOOD" }, warn: { c: "#fbbf24", t: "NEEDS WORK" }, bad: { c: "#f87171", t: "PROBLEM" } };
const mono = { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 11.5, color: "var(--t-text-body)", background: "var(--t-surface-mid, rgba(15,23,42,0.4))", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 8px", wordBreak: "break-all", marginTop: 6 };
const fieldStyle = { background: "var(--t-surface-mid)", border: "1px solid var(--t-border-strong)", borderRadius: 8, padding: "8px 11px", color: "var(--t-text-primary)", fontSize: 13, boxSizing: "border-box" };

function CheckCard({ title, b, children }) {
  const st = STATE[areaState(b)];
  return (
    <div style={{ ...panel, padding: 14, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--t-text-primary)" }}>{title}</div>
        {st && <span style={{ fontSize: 10.5, fontWeight: 800, color: st.c, border: `1px solid ${st.c}55`, borderRadius: 6, padding: "1px 7px" }}>{st.t}{b ? ` · ${b.pts}/${b.max}` : ""}</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--t-text-body)", lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function DomainCheck() {
  const [domain, setDomain] = useState("");
  const [selector, setSelector] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async (e) => {
    e && e.preventDefault();
    if (!domain.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await call("/email-security/domain-check", { method: "POST", body: JSON.stringify({ domain, selector: selector.trim() || undefined }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setRes(j);
    } catch (e2) { setErr(e2.message); setRes(null); }
    setBusy(false);
  };
  const area = (name) => res && res.breakdown.find(b => b.area.startsWith(name));
  const sp = res && SPOOF[res.spoof.level];
  const yes = (v) => <span style={{ color: v ? "#34d399" : "var(--t-text-faint)", fontWeight: 700 }}>{v ? "Yes" : "No"}</span>;

  return (
    <div style={{ ...panel, padding: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: "var(--t-text-primary)" }}>Check any domain</div>
      <div style={{ fontSize: 12, color: "var(--t-text-faint)", margin: "2px 0 10px" }}>
        Checks SPF, DKIM and DMARC from public DNS for any company, then shows whether the domain can be spoofed and whether its email security is being managed properly.
      </div>
      <form onSubmit={run} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <input value={domain} onChange={e => setDomain(e.target.value)} placeholder="Domain or email address, e.g. example.com" style={{ ...fieldStyle, flex: "2 1 240px" }} />
        <input value={selector} onChange={e => setSelector(e.target.value)} placeholder="DKIM selector (optional)" title="From the s= value in a message's DKIM-Signature header" style={{ ...fieldStyle, flex: "1 1 160px" }} />
        <button type="submit" disabled={busy || !domain.trim()} style={btn("#4f6fff", { opacity: busy || !domain.trim() ? 0.6 : 1 })}>{busy ? "Checking…" : "Check"}</button>
      </form>
      {err && <div style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>{err}</div>}

      {res && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "2 1 320px", background: sp.bg, border: `1px solid ${sp.c}55`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: sp.c, letterSpacing: 0.5 }}>{sp.label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t-text-primary)", marginTop: 2 }}>{res.spoof.headline}</div>
              <div style={{ fontSize: 12.5, color: "var(--t-text-body)", marginTop: 3 }}>{res.spoof.detail}</div>
            </div>
            <div style={{ flex: "1 1 220px", ...panel, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, color: "var(--t-text-faint)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Management score · {res.domain}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: scoreColor(res.score), lineHeight: 1.2 }}>{res.score} <span style={{ fontSize: 15 }}>{res.grade}</span></div>
              <div style={{ fontSize: 12, color: MGMT[res.management.level], fontWeight: 700 }}>{res.management.text}</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
            <CheckCard title="DMARC — spoofing policy" b={area("DMARC")}>
              {!res.dmarc.record ? <>No record at _dmarc.{res.domain}</> : <>
                Policy: <b>{res.dmarc.effective_policy || res.dmarc.policy || "invalid"}</b>{res.dmarc.valid && res.dmarc.pct < 100 ? ` (${res.dmarc.pct}%)` : ""}
                {res.dmarc.source === "organization" && <> · inherited from {res.dmarc.org_domain}</>}<br />
                Subdomains: <b>{res.dmarc.subdomain_policy || res.dmarc.policy || "—"}</b><br />
                Reports go to: {res.dmarc.rua && res.dmarc.rua.length ? res.dmarc.rua.join(", ") : <span style={{ color: "#fb923c" }}>nobody</span>}
                <div style={mono}>{res.dmarc.record}</div>
              </>}
            </CheckCard>
            <CheckCard title="SPF — allowed senders" b={area("SPF")}>
              {!res.spf.record ? <>No SPF record</> : <>
                Ends with: <b>{res.spf.all || "no all rule"}</b> · DNS lookups: <b style={{ color: res.spf.lookups > 10 ? "#f87171" : undefined }}>{res.spf.lookups}</b> / 10
                {res.spf.records.length > 1 && <><br /><span style={{ color: "#f87171" }}>{res.spf.records.length} SPF records (only one allowed)</span></>}
                {res.spf.records.map((r, i) => <div key={i} style={mono}>{r}</div>)}
              </>}
            </CheckCard>
            <CheckCard title="DKIM — message signing" b={area("DKIM")}>
              {res.dkim.found.filter(k => !k.revoked).length
                ? res.dkim.found.filter(k => !k.revoked).map(k => <div key={k.selector}><b>{k.selector}</b> · {k.type.toUpperCase()}{k.bits ? ` ${k.bits}-bit` : ""}</div>)
                : <>No key found.</>}
              <div style={{ color: "var(--t-text-faint)", marginTop: 4 }}>
                Checked {res.dkim.checked} common selectors{res.dkim.custom_selector ? `, including "${res.dkim.custom_selector}"` : ""}. DNS can't list every selector — enter one from a sent message's DKIM-Signature header (s=) to check it.
              </div>
            </CheckCard>
            <CheckCard title="Mail servers & extras" b={null}>
              Email provider: <b>{res.mx.provider}</b><br />
              {res.mx.hosts.length > 0 && <div style={{ ...mono, whiteSpace: "pre-line" }}>{res.mx.hosts.join("\n")}</div>}
              <div style={{ marginTop: 6 }}>MTA-STS: {yes(res.extras.mta_sts)} · TLS reporting: {yes(res.extras.tls_rpt)} · BIMI logo: {yes(res.extras.bimi)}</div>
            </CheckCard>
          </div>

          <div style={{ ...panel, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--t-text-primary)", marginBottom: 8 }}>
              {res.findings.filter(f => f.severity !== "info").length ? "What to fix" : "Nothing to fix"}
            </div>
            {res.findings.map((f, i) => {
              const sv = SEV[f.severity] || { c: "#94a3b8", bg: "rgba(148,163,184,0.13)", label: "INFO" };
              return (
                <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid var(--t-border-soft)" : 0 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: sv.c, background: sv.bg, borderRadius: 5, padding: "1px 6px", marginRight: 8 }}>{sv.label}</span>
                  <b style={{ fontSize: 13, color: "var(--t-text-primary)" }}>{f.title}</b>
                  <div style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 3 }}>{f.detail}</div>
                  {f.fix && <div style={{ fontSize: 12, color: "var(--t-text-faint)", marginTop: 2 }}><b>Fix:</b> {f.fix}</div>}
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 8 }}>
              Checked {new Date(res.checked_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET from public DNS.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientEmailSecurity() {
  const [ov, setOv]           = useState(null);
  const [sel, setSel]         = useState(null);      // selected client id
  const [scan, setScan]       = useState(null);
  const [dmarc, setDmarc]     = useState(null);
  const [audit, setAudit]     = useState(null);
  const [mfa, setMfa]         = useState(null);      // latest MFA audit for the selected client
  const [mfaOv, setMfaOv]     = useState([]);        // latest MFA audit per tenant (fleet table)
  const [tab, setTab]         = useState("posture");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState("");
  const [err, setErr]         = useState("");
  const [note, setNote]       = useState("");

  const loadOverview = useCallback(async () => {
    try {
      call("/email-security/mfa/overview").then(r => r.json()).then(m => setMfaOv(m.clients || [])).catch(() => {});
      const j = await call("/email-security/overview").then(r => r.json());
      if (j.error) throw new Error(j.error);
      setOv(j);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const openClient = async (id) => {
    setSel(id); setScan(null); setDmarc(null); setMfa(null); setErr(""); setNote("");
    const [s, d, a, m] = await Promise.all([
      call(`/email-security/scan/${id}`).then(r => r.json()).catch(() => ({})),
      call(`/email-security/dmarc/${id}`).then(r => r.json()).catch(() => ({})),
      call(`/email-security/audit/${id}`).then(r => r.json()).catch(() => ({})),
      call(`/email-security/mfa/${id}`).then(r => r.json()).catch(() => ({})),
    ]);
    setScan(s.scan || null); setDmarc(d || null); setAudit(a || null); setMfa(m || null); setTab("posture");
  };

  const runScan = async (id) => {
    setBusy("scan"); setErr(""); setNote("");
    try {
      const j = await call(`/email-security/scan/${id}`, { method: "POST" }).then(r => r.json());
      if (j.error) throw new Error(j.error);
      setScan(j); setNote(`Scan complete — ${(j.findings || []).length} findings.`);
      loadOverview();
    } catch (e) { setErr(e.message); } finally { setBusy(""); }
  };

  const runAudit = async (id) => {
    setBusy("audit"); setErr(""); setNote("");
    try {
      const j = await call(`/email-security/audit/${id}`, { method: "POST" }).then(r => r.json());
      if (j.error) throw new Error(j.error);
      setAudit({ audit: { ran_at: j.ran_at, ok: true, findings: j.findings }, history: (audit && audit.history) || [] });
      setNote(`Audit complete — ${(j.findings || []).length} mailbox(es) flagged.`);
    } catch (e) { setErr(e.message); } finally { setBusy(""); }
  };

  const runMfa = async (id) => {
    setBusy("mfa"); setErr(""); setNote("");
    try {
      const j = await call(`/email-security/mfa/${id}`, { method: "POST" }).then(r => r.json());
      if (j.error && j.ok === undefined) throw new Error(j.error);
      setMfa(await call(`/email-security/mfa/${id}`).then(r => r.json()));
      setNote(j.ok ? `MFA audit complete — ${j.summary.coverage ?? "?"}% of accounts have MFA registered.` : `MFA audit could not run: ${j.error}`);
      loadOverview();
    } catch (e) { setErr(e.message); } finally { setBusy(""); }
  };

  const exportMfa = async (id, name) => {
    try {
      const r = await call(`/email-security/mfa/${id}/export.csv`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Export failed (${r.status})`);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "client").replace(/[^a-z0-9]+/gi, "-")}-MFA-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e.message); }
  };

  const runTool = async (id, tool, args, confirmText) => {
    if (!window.confirm(`${confirmText}\n\nThis changes the CLIENT'S LIVE TENANT. Continue?`)) return;
    setBusy(tool); setErr(""); setNote("");
    try {
      const j = await call(`/email-security/tools/${id}/${tool}`, { method: "POST", body: JSON.stringify(args || {}) }).then(r => r.json());
      if (j.error) throw new Error(j.error);
      setNote(`✅ ${j.applied} — re-scan to confirm.`);
    } catch (e) { setErr(e.message); } finally { setBusy(""); }
  };

  const selClient = ov && ov.clients.find(c => c.id === sel);
  const mfaOf = Object.fromEntries(mfaOv.map(m => [m.id, m]));
  const mfaDone = mfaOv.filter(m => m.ok && m.summary && m.summary.coverage != null);
  const mfaAvg = mfaDone.length ? Math.round(mfaDone.reduce((t, m) => t + m.summary.coverage, 0) / mfaDone.length) : null;
  const mfaAdmins = mfaDone.reduce((t, m) => t + (m.summary.admins_no_mfa || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "auto", background: "var(--t-page)" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--t-border-soft)" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>
              Client Email Security
            </h1>
            <p style={{ margin: "3px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>
              O365 mail-flow, authentication and mailbox posture across every connected tenant, plus a public check for any domain
            </p>
          </div>
          {sel && <button onClick={() => { setSel(null); setScan(null); }} style={btn("transparent", { color: "var(--t-text-body)", border: "1px solid var(--t-border-soft)" })}>← All clients</button>}
        </div>
      </div>

      <div style={{ padding: "18px 24px 40px", display: "flex", flexDirection: "column", gap: 16 }}>
        {err && <div style={{ ...panel, padding: 12, borderColor: "#b91c1c", background: "rgba(185,28,28,0.12)", color: "#fca5a5", fontSize: 13 }}>{err}</div>}
        {note && <div style={{ ...panel, padding: 12, borderColor: "#16a34a", background: "rgba(22,163,74,0.10)", color: "#bbf7d0", fontSize: 13 }}>{note}</div>}
        {loading && <div style={{ color: "var(--t-text-faint)", fontSize: 13 }}>Loading…</div>}

        {/* ── CHECK ANY DOMAIN ── */}
        {!sel && <DomainCheck />}

        {/* ── DASHBOARD ── */}
        {!sel && ov && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
              <Stat label="Avg posture" value={ov.totals.avg_score ?? "—"} sub={`${ov.totals.scanned}/${ov.totals.clients} scanned`} color={scoreColor(ov.totals.avg_score)} />
              <Stat label="Critical" value={ov.totals.critical} sub="across the estate" color={ov.totals.critical ? "#f87171" : "#34d399"} />
              <Stat label="High" value={ov.totals.high} color={ov.totals.high ? "#fb923c" : "#34d399"} />
              <Stat label="Domains" value={ov.totals.domains} sub={`${ov.totals.with_dmarc} with DMARC`} />
              <Stat label="Mailboxes" value={ov.totals.mailboxes || "—"} sub={`${ov.totals.forwarders || 0} forwarding out`} color={ov.totals.forwarders ? "#fb923c" : undefined} />
              <Stat label="DMARC reports" value={ov.dmarc?.reports || 0} sub={ov.dmarc?.latest ? `latest ${ago(ov.dmarc.latest)}` : "none received yet"} />
              <Stat label="MFA registered" value={mfaAvg != null ? `${mfaAvg}%` : "—"} sub={mfaDone.length ? `${mfaAdmins} admin(s) without MFA` : "no MFA audit yet"} color={mfaAdmins ? "#f87171" : scoreColor(mfaAvg)} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
              <div style={{ ...panel, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 12 }}>Estate-wide authentication coverage</div>
                <Bar label="SPF published"      have={ov.totals.with_spf}         total={ov.totals.domains} />
                <Bar label="DKIM selectors"     have={ov.totals.with_dkim}        total={ov.totals.domains} />
                <Bar label="DMARC present"      have={ov.totals.with_dmarc}       total={ov.totals.domains} />
                <Bar label="DMARC enforcing"    have={ov.totals.enforcing_dmarc}  total={ov.totals.domains} />
                <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 8, lineHeight: 1.6 }}>
                  A domain is only protected from spoofing once DMARC is <b>enforcing</b>. SPF alone never checks the From: header.
                </div>
              </div>

              <div style={{ ...panel, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 12 }}>Most widespread issues</div>
                {(ov.common || []).length === 0 && <div style={{ fontSize: 12, color: "var(--t-text-faint)" }}>Nothing found yet — run a scan.</div>}
                {(ov.common || []).map(c => (
                  <div key={c.type} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: SEV[c.severity]?.bg, color: SEV[c.severity]?.c, flexShrink: 0 }}>{SEV[c.severity]?.label}</span>
                    <span style={{ fontSize: 12, color: "var(--t-text-body)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.example}>{c.label || c.example}</span>
                    <span style={{ fontSize: 11, color: "var(--t-text-faint)", flexShrink: 0, whiteSpace: "nowrap" }}>{c.count}× · {c.clients} client{c.clients === 1 ? "" : "s"}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Client table */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>Connected tenants ({ov.clients.length})</div>
              <div style={{ ...panel, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase" }}>
                      {["Client", "Score", "Critical", "High", "Domains", "DMARC", "DKIM", "Mailboxes", "MFA", "Last scan"].map(h =>
                        <th key={h} style={{ padding: "9px 12px", borderBottom: "1px solid var(--t-border-soft)", fontWeight: 600 }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {ov.clients.map(c => (
                      <tr key={c.id} onClick={() => openClient(c.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)" }}>
                        <td style={{ padding: "9px 12px", fontWeight: 600, color: "var(--t-text-primary)" }}>{c.company_name}</td>
                        <td style={{ padding: "9px 12px", fontWeight: 800, color: scoreColor(c.score) }}>{c.score ?? "—"}</td>
                        <td style={{ padding: "9px 12px", color: c.critical ? "#f87171" : "var(--t-text-faint)", fontWeight: c.critical ? 700 : 400 }}>{c.ran_at ? c.critical : "—"}</td>
                        <td style={{ padding: "9px 12px", color: c.high ? "#fb923c" : "var(--t-text-faint)" }}>{c.ran_at ? c.high : "—"}</td>
                        <td style={{ padding: "9px 12px", color: "var(--t-text-body)" }}>{c.domains || "—"}</td>
                        <td style={{ padding: "9px 12px", color: "var(--t-text-body)" }}>{c.ran_at ? `${c.with_dmarc}/${c.domains}` : "—"}</td>
                        <td style={{ padding: "9px 12px", color: "var(--t-text-body)" }}>{c.ran_at ? `${c.with_dkim}/${c.domains}` : "—"}</td>
                        <td style={{ padding: "9px 12px", color: "var(--t-text-body)" }}>{c.mailboxes ?? "—"}</td>
                        {(() => {
                          const m = mfaOf[c.id];
                          if (!m || !m.ran_at) return <td style={{ padding: "9px 12px", color: "var(--t-text-faint)" }}>—</td>;
                          if (!m.ok) return <td title={m.error} style={{ padding: "9px 12px", color: "#fbbf24" }}>can't read</td>;
                          return (
                            <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 800, color: scoreColor(m.summary.coverage) }}>{m.summary.coverage ?? "?"}%</span>
                              {m.summary.admins_no_mfa > 0 && <span style={{ color: "#f87171", fontSize: 11, fontWeight: 700 }}> · {m.summary.admins_no_mfa} admin</span>}
                            </td>
                          );
                        })()}
                        <td style={{ padding: "9px 12px", color: c.ran_at ? "var(--t-text-faint)" : "#fbbf24" }}>{c.ran_at ? ago(c.ran_at) : "never scanned"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── CLIENT DETAIL ── */}
        {sel && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "var(--t-text-primary)" }}>{selClient?.company_name}</div>
                <div style={{ fontSize: 12, color: "var(--t-text-faint)" }}>
                  {scan?.ran_at ? `Last scanned ${ago(scan.ran_at)}` : "Never scanned"}
                  {scan?.summary?.mailboxes != null && ` · ${scan.summary.mailboxes} mailboxes`}
                </div>
              </div>
              <button onClick={() => runScan(sel)} disabled={busy === "scan"} style={btn("#2563eb", { opacity: busy === "scan" ? 0.6 : 1 })}>
                {busy === "scan" ? "Scanning tenant… (up to 2 min)" : "Run scan"}
              </button>
            </div>

            {scan?.exoError && (
              <div style={{ ...panel, padding: 12, borderColor: "#b45309", background: "rgba(180,83,9,0.12)", color: "#fbbf24", fontSize: 12 }}>
                Exchange read failed: {scan.exoError}
              </div>
            )}

            {tab === "posture" && scan?.summary && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
                <Stat label="Critical" value={scan.summary.critical} color={scan.summary.critical ? "#f87171" : "#34d399"} />
                <Stat label="High" value={scan.summary.high} color={scan.summary.high ? "#fb923c" : "#34d399"} />
                <Stat label="Domains" value={scan.summary.domains} />
                <Stat label="DMARC" value={`${scan.summary.with_dmarc}/${scan.summary.domains}`} sub={`${scan.summary.enforcing_dmarc} enforcing`} />
                <Stat label="DKIM" value={`${scan.summary.with_dkim}/${scan.summary.domains}`} />
                <Stat label="Forwarders" value={scan.summary.forwarders ?? "—"} color={scan.summary.forwarders ? "#fb923c" : undefined} />
              </div>
            )}

            {/* Findings + fix tools */}
            {tab === "posture" && scan?.findings && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>Findings ({scan.findings.length})</div>
                {scan.findings.length === 0 && <div style={{ ...panel, padding: 14, fontSize: 13, color: "#bbf7d0" }}>No findings — this tenant is clean.</div>}
                <div style={{ display: "grid", gap: 8 }}>
                  {scan.findings.map((f, i) => (
                    <div key={i} style={{ ...panel, padding: 13, borderLeft: `3px solid ${SEV[f.severity]?.c}` }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: SEV[f.severity]?.bg, color: SEV[f.severity]?.c }}>{SEV[f.severity]?.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>{f.title}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 4, lineHeight: 1.6 }}>{f.detail}</div>
                        </div>
                        {f.tool && (
                          <button onClick={() => runTool(sel, f.tool, f.args, f.title)} disabled={!!busy}
                            style={btn("#16a34a", { flexShrink: 0, opacity: busy ? 0.6 : 1 })}>
                            {busy === f.tool ? "Applying…" : "Fix"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-domain authentication table */}
            {tab === "posture" && scan?.domains && scan.domains.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>Domain authentication ({scan.domains.length})</div>
                <div style={{ ...panel, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 620 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase" }}>
                        {["Domain", "SPF", "DKIM", "DMARC", "Reports to"].map(h =>
                          <th key={h} style={{ padding: "8px 12px", borderBottom: "1px solid var(--t-border-soft)", fontWeight: 600 }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {scan.domains.map(d => {
                        const dk = d.dkim_selector1 || d.dkim_selector2;
                        const enforcing = d.dmarc_policy && d.dmarc_policy !== "none";
                        return (
                          <tr key={d.domain} style={{ borderBottom: "1px solid var(--t-border-soft)" }}>
                            <td style={{ padding: "8px 12px", fontFamily: "ui-monospace,Menlo,monospace", color: "var(--t-text-primary)" }}>{d.domain}</td>
                            <td style={{ padding: "8px 12px", color: d.spf ? (["-all", "~all"].includes(d.spf_all) ? "#34d399" : "#fbbf24") : "#f87171" }}>{d.spf ? (d.spf_all || "set") : "missing"}</td>
                            <td style={{ padding: "8px 12px", color: dk ? "#34d399" : "#f87171" }}>{dk ? "published" : "missing"}</td>
                            <td style={{ padding: "8px 12px", color: enforcing ? "#34d399" : d.dmarc ? "#fbbf24" : "#f87171", fontWeight: enforcing ? 700 : 400 }}>{d.dmarc_policy ? `p=${d.dmarc_policy}` : "missing"}</td>
                            <td style={{ padding: "8px 12px", color: "var(--t-text-faint)", fontSize: 11, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.dmarc_rua || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* DMARC report sources — who is sending as these domains */}
            {tab === "posture" && <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>
                Who is sending as these domains <span style={{ fontWeight: 400, color: "var(--t-text-faint)" }}>· from DMARC aggregate reports</span>
              </div>
              {!dmarc || !dmarc.sources || dmarc.sources.length === 0 ? (
                <div style={{ ...panel, padding: 14, fontSize: 12, color: "var(--t-text-faint)", lineHeight: 1.7 }}>
                  No reports yet. Aggregate reports begin arriving roughly 24 hours after a <code>rua</code> record is published, and land daily thereafter.
                  Unaligned sources listed here are the ones that would be rejected if DMARC moved to enforcement — check each is legitimate before tightening policy.
                </div>
              ) : (
                <div style={{ ...panel, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 620 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--t-text-faint)", fontSize: 11, textTransform: "uppercase" }}>
                        {["Source IP", "Domain", "Messages", "Aligned", "Unaligned", "Last seen"].map(h =>
                          <th key={h} style={{ padding: "8px 12px", borderBottom: "1px solid var(--t-border-soft)", fontWeight: 600 }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {dmarc.sources.map((s, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid var(--t-border-soft)" }}>
                          <td style={{ padding: "8px 12px", fontFamily: "ui-monospace,Menlo,monospace", color: "var(--t-text-primary)" }}>{s.source_ip}</td>
                          <td style={{ padding: "8px 12px", color: "var(--t-text-body)" }}>{s.domain}</td>
                          <td style={{ padding: "8px 12px", color: "var(--t-text-body)" }}>{s.volume}</td>
                          <td style={{ padding: "8px 12px", color: "#34d399" }}>{s.aligned}</td>
                          <td style={{ padding: "8px 12px", color: s.unaligned ? "#f87171" : "var(--t-text-faint)", fontWeight: s.unaligned ? 700 : 400 }}>{s.unaligned}</td>
                          <td style={{ padding: "8px 12px", color: "var(--t-text-faint)" }}>{ago(s.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>}

            {/* Tabs */}
            <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--t-border-soft)", marginBottom: 2 }}>
              {[["posture", "Posture"], ["audit", `Audit${audit?.audit?.findings?.length ? ` (${audit.audit.findings.length})` : ""}`], ["mfa", `MFA${mfa?.audit?.summary?.admins_no_mfa ? ` (${mfa.audit.summary.admins_no_mfa} admin)` : ""}`]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} style={{
                  background: "transparent", border: 0, borderBottom: tab === k ? "2px solid #4f6fff" : "2px solid transparent",
                  color: tab === k ? "var(--t-text-primary)" : "var(--t-text-faint)", fontSize: 13, fontWeight: 700,
                  padding: "8px 14px", cursor: "pointer",
                }}>{label}</button>
              ))}
            </div>

            {/* ── AUDIT TAB ── */}
            {tab === "audit" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--t-text-faint)", lineHeight: 1.7, maxWidth: 640 }}>
                    Looks for evidence this tenant is <b>sending</b> spam or phishing — a compromised mailbox — rather than
                    receiving it. Runs automatically every 5 hours and alerts juwan@ and zach.hartley@ when the flagged set changes.
                    {audit?.audit?.ran_at && <> Last run {ago(audit.audit.ran_at)}.</>}
                  </div>
                  <button onClick={() => runAudit(sel)} disabled={!!busy} style={btn("#7c3aed", { opacity: busy ? 0.6 : 1 })}>
                    {busy === "audit" ? "Auditing mailboxes… (up to 5 min)" : "Run audit now"}
                  </button>
                </div>

                {audit?.audit?.error && (
                  <div style={{ ...panel, padding: 12, borderColor: "#b45309", background: "rgba(180,83,9,0.12)", color: "#fbbf24", fontSize: 12 }}>
                    Last audit failed: {audit.audit.error}
                  </div>
                )}

                {!audit?.audit && <div style={{ ...panel, padding: 16, fontSize: 13, color: "var(--t-text-faint)" }}>No audit has run for this tenant yet.</div>}

                {audit?.audit && (audit.audit.findings || []).length === 0 && (
                  <div style={{ ...panel, padding: 16, fontSize: 13, color: "#bbf7d0" }}>
                    No compromise indicators found — no restricted senders, no external auto-forwarding, no suspicious inbox rules, no outbound spam.
                  </div>
                )}

                {(audit?.audit?.findings || []).map((f, i) => (
                  <div key={i} style={{ ...panel, padding: 14, borderLeft: `3px solid ${SEV[f.severity]?.c}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: SEV[f.severity]?.bg, color: SEV[f.severity]?.c }}>{SEV[f.severity]?.label}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)", fontFamily: "ui-monospace,Menlo,monospace" }}>{f.mailbox}</span>
                        </div>
                        {(f.reasons || []).map((r, j) => (
                          <div key={j} style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 5, lineHeight: 1.65 }}>• {r.detail}</div>
                        ))}
                      </div>
                      {f.fix && (
                        <button
                          onClick={() => runTool(sel, f.fix, f.fixArgs, `Execute recommended fix on ${f.mailbox}:\n\n${(f.reasons || []).map(r => "• " + r.type.replace(/_/g, " ")).join("\n")}`)}
                          disabled={!!busy}
                          style={btn("#16a34a", { flexShrink: 0, opacity: busy ? 0.6 : 1 })}>
                          {busy === f.fix ? "Applying…" : "Execute recommended fix"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {(audit?.history || []).length > 1 && (
                  <div style={{ fontSize: 11, color: "var(--t-text-faint)" }}>
                    Recent runs: {audit.history.map((h, i) => `${ago(h.ran_at)}${h.ok ? ` (${h.n})` : " ✗"}`).join(" · ")}
                  </div>
                )}
              </div>
            )}

            {/* ── MFA TAB ── */}
            {tab === "mfa" && (
              <MfaPanel mfa={mfa} busy={busy} onRun={() => runMfa(sel)} onExport={() => exportMfa(sel, selClient?.company_name)} />
            )}

            {/* Standalone tools */}
            <div style={{ ...panel, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 4 }}>Tools</div>
              <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginBottom: 12 }}>
                Each writes to the live tenant and is logged to the audit trail.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => runTool(sel, "external_tagging", { enabled: true }, "Enable external sender tagging in Outlook")} disabled={!!busy} style={btn("#2563eb")}>Enable external tagging</button>
                <button onClick={() => {
                  const e = window.prompt("Block sender domain(s) or address(es) at the tenant edge — comma separated.\nExample: badactor.com, spoof@example.net");
                  if (e) runTool(sel, "block_sender", { entries: e.split(",").map(x => x.trim()).filter(Boolean) }, `Block: ${e}`);
                }} disabled={!!busy} style={btn("#b91c1c")}>Block sender / domain</button>
                <button onClick={() => {
                  const d = window.prompt("Create a DKIM signing config for which domain?");
                  if (d) runTool(sel, "enable_dkim", { domain: d.trim() }, `Create DKIM config for ${d}`);
                }} disabled={!!busy} style={btn("#7c3aed")}>Create DKIM config</button>
                <button onClick={() => {
                  const d = window.prompt("Activate DKIM signing for which domain?\n⚠️ The selector CNAMEs must already resolve in DNS.");
                  if (d) runTool(sel, "activate_dkim", { domain: d.trim() }, `Enable DKIM signing for ${d}`);
                }} disabled={!!busy} style={btn("#7c3aed")}>Activate DKIM</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
